import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import ProductBadge from 'components/padi-product/badge';
import Image from 'frontastic/lib/image';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import NextLink from 'next/link';
import { useParams } from 'next/navigation';
import { FC, useMemo, useState, type JSX } from 'react';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { ProjectConfig } from 'types/project-config';
import Prices from './prices';
import useTrack from './useTrack';

interface CertProductTileProps {
  product: Product;
  titleHtag?: string;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  projectConfig?: ProjectConfig;
  imagePriority?: boolean;
}

const CertProductTile: FC<CertProductTileProps> = ({
  product,
  titleHtag,
  onClick,
  isSearchResult = false,
  disableWishlistButton = false,
  imagePriority = false,
}) => {
  const { locale } = useParams();
  const { ref } = useTrack({ product });

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);

  const discountedPrice = selectedVariant?.discountedPrice;

  const excerpt = useMemo(() => {
    const field = product.variants?.[0].attributes?.['card-excerpt'];
    return typeof field === 'string' ? field : '';
  }, [product]);

  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  const productUrl = useMemo(() => {
    if (!product._url) return `/${(product.name ?? '').toLowerCase().replace(/\s+/g, '-')}/p/${selectedVariant.sku}`;

    const url = `${product._url}` + (isSearchResult ? '?sr=1' : '');
    return url;
  }, [product, selectedVariant, isSearchResult]);

  const TitleHtag = titleHtag ? (titleHtag as keyof JSX.IntrinsicElements) : 'div';

  // Helper to safely fetch attribute values with a default value
  const getAttributeValue = (attr: any, defaultValue = '') => {
    if (Array.isArray(attr)) return attr[0]?.label ?? defaultValue;
    else return attr?.label ?? defaultValue;
  };
  const cardType = getAttributeValue(product?.variants?.[0]?.attributes?.['card-type']);
  const cardGroup = useMemo(() => {
    const group = getAttributeValue(product?.variants?.[0]?.attributes?.['card-groups']);
    return group !== 'Default' ? group : '';
  }, [product]);

  return (
    <div
      onClick={onClick}
      ref={ref}
      className="product-tile group relative flex h-full flex-col overflow-hidden rounded-none border border-gray-200 bg-white shadow-sm transition delay-200 ease-linear hover:shadow-md"
    >
      <div className="relative">
        <NextLink href={`/${locale}${productUrl}`} className="pb-16">
          <div className="relative w-full">
            <div className="relative bg-white p-0">
              <div className="relative block aspect-[16/9] w-full">
                <Image
                  src={selectedVariant.images?.[0]}
                  alt={product.name ?? ''}
                  className="h-full w-full object-cover group-hover:opacity-75"
                  priority={imagePriority}
                  style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                />
              </div>
            </div>
            <ProductBadge product={product} />
            <span
              className="absolute right-0 top-0 z-10 flex size-32 cursor-pointer items-center justify-center md:size-48"
              onClick={(e) => e.preventDefault()}
            >
              {!disableWishlistButton && productToWishlistLineItem && (
                <WishlistButton lineItem={productToWishlistLineItem} className="size-16 md:size-20 lg:size-24" />
              )}
            </span>
          </div>
        </NextLink>
      </div>

      <NextLink href={`/${locale}${productUrl}`} className="flex-1">
        <div className="flex h-full flex-col justify-between px-8 pb-8 md:px-16 md:pb-16">
          <div className="pt-12">
            {cardGroup && <p className="text-sm font-bold text-padi-gray-darker">{cardGroup}</p>}
            <TitleHtag className="mt-4 block w-full whitespace-pre-line text-wrap text-base font-semibold leading-loose">
              {product?.name}
            </TitleHtag>
          </div>
          <div className="mt-2 text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: excerpt }} />
          <p className="py-12 text-sm font-bold not-italic text-slate-700">{cardType}</p>
          <div className="mt-8 md:mt-12">
            <Prices price={selectedVariant?.price} discountedPrice={discountedPrice} />
          </div>
        </div>
      </NextLink>
    </div>
  );
};

export default CertProductTile;
